import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import api from '../utils/api';
import InfoModal from '../components/common/InfoModal';
import { NAME_LIMITS } from '../utils/nameLimits';
import { translateKnown } from '../i18n/knownMessages';
import { useTranslation, Trans } from 'react-i18next';
import { validatePassword, passwordStrength, translatedProblem } from '../utils/passwordPolicy';
import GoogleSignInButton from '../components/GoogleSignInButton';
import SiteFooter from '../components/common/SiteFooter';
import { saveLoginSession } from '../utils/saveLoginSession';

// Backend message for an email that already has an account (no error code is sent)
const EMAIL_TAKEN = 'Email is already registered.';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [supplierName, setSupplierName] = useState('');

  const [error, setError] = useState('');

  // Google sign-up creates a member account and signs the user in
  const handleGoogle = async (credential) => {
    setError('');
    try {
      const res = await api.post('/api/users/google', { credential });
      saveLoginSession(res.data);
      navigate('/home', { replace: true });
    } catch (err) {
      // Existing password account with this email: point to Sign in, which links Google after the password
      if (err.response?.data?.code === 'google_link_required') {
        setError(EMAIL_TAKEN);
        return;
      }
      setError(err.response?.data?.message || 'Google sign-up failed. Please try again.');
    }
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side password validations
    const passwordProblem = validatePassword(password);
    if (passwordProblem) {
      setError(translatedProblem(t, passwordProblem));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('password.mismatch'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const res = await api.post('/api/users/register', {
        username,
        email,
        password,
        isAdmin,
        ...(isAdmin && supplierName.trim() && { supplierName: supplierName.trim() }),
      });

      if (res.status === 201 || res.status === 200) {
        setShowSuccessModal(true);
      } else {
        setError(res.data.message || 'Registration failed.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      const message = err.response?.data?.message || 'Server error. Please try again later.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white p-8 rounded-lg shadow-md border border-gray-200">
        <h2 className="text-2xl font-semibold text-center text-gray-800 mb-6">
          {t('auth.register')}
        </h2>

        {error && (
          <div className="text-red-500 text-center mb-4">
            {error === EMAIL_TAKEN ? (
              <Trans
                i18nKey="messages.accountExists"
                components={{ 1: <Link to="/login" className="text-blue-600 hover:underline" /> }}
              />
            ) : (
              translateKnown(t, error)
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div>
            <label htmlFor="username" className="sr-only">{t('register.username')}</label>
            <input
              id="username"
              type="text"
              placeholder={t('register.username')}
              maxLength={NAME_LIMITS.username.max}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoComplete="username"
            />
          </div>

          <div>
            <label htmlFor="email" className="sr-only">{t('register.email')}</label>
            <input
              id="email"
              type="email"
              placeholder={t('register.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoComplete="email"
            />
          </div>

          <div className="relative">
            <label htmlFor="password" className="sr-only">{t('register.password')}</label>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder={t('register.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
              autoComplete="new-password"
              aria-describedby="password-guidance"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800"
              aria-label={t('auth.togglePassword')}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          {password && (
            <p className="text-xs text-gray-600 -mt-2">
              {t('password.strength')}: <span className={`font-medium ${{ weak: 'text-red-600', medium: 'text-yellow-600', strong: 'text-green-600' }[passwordStrength(password)]}`}>{t('password.' + passwordStrength(password))}</span>
            </p>
          )}
          <p id="password-guidance" className="text-sm text-gray-600 -mt-2">{t('password.guidance')}</p>

          <div className="relative">
            <label htmlFor="confirmPassword" className="sr-only">{t('register.confirmPassword')}</label>
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder={t('register.confirmPassword')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800"
              aria-label={t('auth.toggleConfirm')}
            >
              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          {confirmPassword && confirmPassword !== password && (
            <p className="text-xs text-red-600 -mt-2">{t('password.confirmMismatch')}</p>
          )}

          <div className="flex items-center space-x-2">
            <input
              id="isAdmin"
              type="checkbox"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="isAdmin" className="text-gray-600 text-sm">
              {t('register.asAdmin')}
            </label>
          </div>

          {isAdmin && (
            <div>
              <input
                id="supplierName"
                type="text"
                placeholder={t('dialogs.register.supplierPlaceholder')}
                maxLength={NAME_LIMITS.supplierName.max}
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={t('dialogs.register.supplierLabel')}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-2 rounded-md text-white transition duration-200 ${
              isSubmitting
                ? 'bg-green-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isSubmitting ? t('register.registering') : t('auth.register')}
          </button>

          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full bg-gray-200 text-gray-800 py-2 rounded-md hover:bg-gray-300 transition duration-200"
          >
            {t('register.backToLogin')}
          </button>
        </form>
        <div className="mt-4">
          <GoogleSignInButton onCredential={handleGoogle} onError={setError} text="signup_with" />
        </div>
      </div>

      <SiteFooter />

      <InfoModal
        isOpen={showSuccessModal}
        onClose={handleModalClose}
        title={translateKnown(t, 'Registration Successful')}
        message={t('register.accountCreated')}
      />
    </div>
  );
}
